/* ============================================================
   S3 Clothing — faceted filtering
   Desktop dropdowns, mobile drawer, and (Task 7) no-reload
   filtering via the Section Rendering API.
   Progressive enhancement: without this file the facet form
   submits as a plain GET.
   ============================================================ */
(function () {
  'use strict';

  document.documentElement.classList.add('js-facets');

  var MOBILE = window.matchMedia('(max-width: 768px)');

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.baseUrl = this.dataset.url;
      this.form = this.querySelector('[data-facets-form]');
      this.drawer = this.querySelector('[data-drawer]');
      this.openBtn = this.querySelector('[data-drawer-open]');
      this.grid = document.getElementById('ProductGrid');
      this.debounceTimer = null;
      this.boundTrap = this.trapFocus.bind(this);
      if (!this.form || !this.drawer) return;

      this.abort = new AbortController();
      var signal = this.abort.signal;

      /* filter changes */
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.apply(); }, { signal: signal });
      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-price-input]')) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => this.apply(), 500);
        } else {
          this.apply();
        }
      }, { signal: signal });

      /* chips, clear-all, and desktop outside-click close (delegated:
         the chips row is re-rendered after every update) */
      document.addEventListener('click', (e) => {
        var link = e.target.closest('[data-chip], [data-clear]');
        if (link) { e.preventDefault(); this.render(link.href, true); return; }
        if (!MOBILE.matches) {
          this.querySelectorAll('details[data-facet][open]').forEach((d) => {
            if (!d.contains(e.target)) d.removeAttribute('open');
          });
        }
      }, { signal: signal });

      /* Escape closes drawer or open dropdown */
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (this.drawerIsOpen()) { this.closeDrawer(); return; }
        var open = this.querySelector('details[data-facet][open]');
        if (open) { open.removeAttribute('open'); open.querySelector('summary').focus(); }
      }, { signal: signal });

      /* mobile drawer */
      if (this.openBtn) this.openBtn.addEventListener('click', () => this.openDrawer(), { signal: signal });
      this.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeDrawer(), { signal: signal });
      });

      /* back/forward */
      window.addEventListener('popstate', () => this.render(window.location.href, false), { signal: signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.controller) this.controller.abort();
    }

    /* ---------- drawer ---------- */

    drawerIsOpen() { return this.drawer.classList.contains('is-open'); }

    openDrawer() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('role', 'dialog');
      this.drawer.setAttribute('aria-modal', 'true');
      this.openBtn.setAttribute('aria-expanded', 'true');
      document.body.classList.add('facet-drawer-open');
      this.drawer.addEventListener('keydown', this.boundTrap);
      var first = this.drawer.querySelector('button, summary, input:not([disabled]), a[href]');
      if (first) first.focus();
    }

    closeDrawer() {
      this.drawer.classList.remove('is-open');
      this.drawer.removeAttribute('role');
      this.drawer.removeAttribute('aria-modal');
      this.openBtn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('facet-drawer-open');
      this.drawer.removeEventListener('keydown', this.boundTrap);
      this.openBtn.focus();
    }

    trapFocus(e) {
      if (e.key !== 'Tab') return;
      var items = this.drawer.querySelectorAll(
        'summary, input:not([disabled]), button, a[href], select'
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- filtering via Section Rendering API ---------- */

    buildUrl() {
      var params = new URLSearchParams();
      new FormData(this.form).forEach(function (value, key) {
        if (value !== '') params.append(key, value);
      });
      return this.baseUrl + '?' + params.toString();
    }

    apply() { this.render(this.buildUrl(), true); }

    render(url, push) {
      var self = this;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      var u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      this.setLoading(true);
      fetch(u.toString(), { signal: this.controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          self.swapRegions(doc);
          if (push) {
            u.searchParams.delete('section_id');
            var qs = u.searchParams.toString();
            history.pushState({}, '', u.pathname + (qs ? '?' + qs : ''));
          }
          self.setLoading(false);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          /* graceful fallback: full navigation */
          window.location.href = url;
        });
    }

    swapRegions(doc) {
      var self = this;

      var newGrid = doc.getElementById('ProductGrid');
      if (newGrid && this.grid) this.grid.innerHTML = newGrid.innerHTML;

      var chips = document.getElementById('FacetChips');
      var newChips = doc.getElementById('FacetChips');
      if (chips && newChips) {
        chips.innerHTML = newChips.innerHTML;
        chips.className = newChips.className;
      }

      /* update text only, so the aria-live region node persists */
      var count = document.getElementById('ResultCount');
      var newCount = doc.getElementById('ResultCount');
      if (count && newCount) count.textContent = newCount.textContent;

      /* refresh each facet panel's options in place (keeps open dropdowns
         open); skip the panel the user is currently typing/clicking in */
      this.querySelectorAll('[data-facet-panel]').forEach(function (panel) {
        if (panel.contains(document.activeElement)) return;
        var fresh = doc.querySelector(
          '[data-facet-panel="' + CSS.escape(panel.dataset.facetPanel) + '"]'
        );
        if (fresh) panel.innerHTML = fresh.innerHTML;
      });

      /* group buttons: active-count badge + aria-label */
      this.querySelectorAll('[data-facet-summary]').forEach(function (s) {
        var fresh = doc.querySelector(
          '[data-facet-summary="' + CSS.escape(s.dataset.facetSummary) + '"]'
        );
        if (!fresh) return;
        s.innerHTML = fresh.innerHTML;
        if (fresh.hasAttribute('aria-label')) {
          s.setAttribute('aria-label', fresh.getAttribute('aria-label'));
        } else {
          s.removeAttribute('aria-label');
        }
      });

      /* "Filter (n)" button and "Show N results" labels */
      ['[data-drawer-open]', '[data-show-results]'].forEach(function (sel) {
        var el = self.querySelector(sel);
        var fresh = doc.querySelector(sel);
        if (el && fresh) el.textContent = fresh.textContent.trim();
      });

      /* keep the sort select in sync (e.g. after Back/Forward) */
      var sortSelect = document.getElementById('SortBy');
      var freshSort = doc.getElementById('SortBy');
      if (sortSelect && freshSort) sortSelect.value = freshSort.value;
    }

    setLoading(on) {
      if (!this.grid) return;
      this.grid.classList.toggle('is-loading', on);
      this.grid.setAttribute('aria-busy', on ? 'true' : 'false');
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
